<script lang="ts">
/**
 * Renders a translation that contains inline HTML (formatting, links).
 *
 * Thin wrapper over `$tHtml` — resolves the key, interpolates params and
 * sanitizes the result (inline allowlist) before rendering. Use for short
 * translated strings like terms labels or GDPR links. Pass `block` for longer
 * editorial translations that CMS editors format with `<p>`, `<ul>`, … themselves.
 *
 * Implemented as a render function so the wrapper tag can be dynamic without
 * tripping `vue/no-v-html` on a `<component>` element; the HTML is already
 * sanitized by `$tHtml`.
 *
 * @example
 * <I18nHtml key-name="ibe.terms.privacy-policy" fallback="… <a href='/gdpr'>policy</a>." />
 * <I18nHtml key-name="ibe.foo" :params="{ url }" tag="p" />
 * <I18nHtml key-name="ibe.modal.more-options-content" block tag="div" />
 */
import { computed, defineComponent, h } from 'vue';
import {
  $tHtml,
  $tHtmlBlock,
  type TranslationParams,
} from '@/composables/translations/useTranslate';

export default defineComponent({
  name: 'I18nHtml',
  props: {
    /** Translation key — named `keyName` because `key` is reserved by Vue. */
    keyName: { type: String, required: true },
    fallback: { type: String, default: undefined },
    params: { type: Object as () => TranslationParams, default: undefined },
    /** Wrapper element to render. Defaults to an inline `span`. */
    tag: { type: String, default: 'span' },
    /** Keep block-level tags (`p`, `ul`, `li`, …) instead of the inline-only allowlist. */
    block: { type: Boolean, default: false },
  },
  setup(props) {
    const html = computed(() =>
      props.block
        ? $tHtmlBlock(props.keyName, props.fallback, props.params)
        : $tHtml(props.keyName, props.fallback, props.params)
    );
    return () => h(props.tag, { innerHTML: html.value });
  },
});
</script>
